import React, {
  ChangeEvent,
  FunctionComponent,
  useCallback,
  useEffect,
} from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import { ApiState, ApiErrorReasonType } from 'scenes/common/types';
import {
  MotorFullPageProps,
  VehicleSearchType,
  MotorFunnelState,
} from 'scenes/motor/types';

import { SubmitButton } from 'components';
import { Button } from 'components/chroma';

import { InputQuestion } from 'scenes/common/components/Questions';
import { getApiErrorMessage, isApiError } from 'scenes/common/helpers/apiData';

import { getLicencePlate, getVehicleFromSearch } from 'scenes/motor/selectors';

import VehicleNotFound from './VehicleNotFound';

const MIN_LENGTH = 2;
const MAX_LENGTH = 10;
const ALPHANUMERIC_PATTERN = /^[A-Za-z0-9]*$/;

const LICENCE_PLATE = 'licencePlate';

const ManualSelectionButtonWrapper = styled.div`
  text-align: center;
`;

const alreadyFound = (
  licencePlate: string | undefined,
  state: MotorFunnelState
): boolean =>
  getVehicleFromSearch(state)?.plateNumber.toLowerCase() ===
  licencePlate?.toLowerCase();

const RegoSearchStep: FunctionComponent<MotorFullPageProps> = ({
  funnelState,
  onNext,
  onRegoSearch,
}) => {
  const {
    formState: hooksForm,
    handleSubmit,
    setError,
    errors,
    control,
    getValues,
  } = useForm({
    mode: 'onChange',
  });
  const goToVehicleManualSelect = useCallback(() => {
    onNext({ vehicleSearchType: VehicleSearchType.ManualEntry });
  }, [onNext]);

  const onSubmit = useCallback(() => {
    const plate = getValues(LICENCE_PLATE);

    if (alreadyFound(plate, funnelState)) {
      onNext({
        vehicleSearchType: VehicleSearchType.RegoLookup,
      });
    } else if (plate) {
      onRegoSearch(plate);
    }
  }, [getValues, funnelState, onNext, onRegoSearch]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, onChange: Function): void => {
      const { value } = event.target;

      if (value.match(ALPHANUMERIC_PATTERN)) {
        onChange(value);
      }
    },
    []
  );

  useEffect(() => {
    if (isApiError(funnelState.apiResponses.foundVehicle)) {
      setError(LICENCE_PLATE, {
        type: 'manual',
        message: getApiErrorMessage(funnelState.apiResponses.foundVehicle),
      });
    }
  }, [funnelState.apiResponses.foundVehicle, setError]);

  return funnelState.apiResponses.foundVehicle.errorReason?.type !==
    ApiErrorReasonType.ApiLimitReached ? (
    <form onSubmit={handleSubmit(onSubmit)}>
      <InputQuestion
        errors={errors}
        control={control}
        maxLength={MAX_LENGTH}
        stateKey={LICENCE_PLATE}
        ariaRequired
        placeholder="e.g. RF52000"
        changeHandler={handleChange}
        validationRules={{
          required: true,
          minLength: MIN_LENGTH,
        }}
        question="What is the registration number?"
        defaultValue={getLicencePlate(funnelState)}
        loading={
          funnelState.apiResponses.foundVehicle.state ===
          ApiState.LoadingWithNoData
        }
        helpText="The car registration must be between 2 and 10 alpha numeric characters."
      />
      <SubmitButton enabled={hooksForm.isValid}>Find car</SubmitButton>
      <ManualSelectionButtonWrapper>
        <Button
          secondary
          maxWidth
          margin={{ top: 3 }}
          onClick={goToVehicleManualSelect}
        >
          Select the car manually
        </Button>
      </ManualSelectionButtonWrapper>
    </form>
  ) : (
    <VehicleNotFound onClick={goToVehicleManualSelect} />
  );
};

export default RegoSearchStep;
